const GITHUB_MAIN = "https://github.com/markitics/agenda/blob/main";

export interface HowBuiltSource {
  label: string;
  href: string;
}

export interface HowBuiltSectionMeta {
  id: string;
  nav: string;
  kicker: string;
  title: string;
  summary: string;
  sources: HowBuiltSource[];
}

export interface HowBuiltStackItem {
  name: string;
  detail: string;
  source: HowBuiltSource;
}

export interface HowBuiltModelItem {
  name: string;
  codeName: string;
  meaning: string;
  importantFields: string;
  ownedBy: string;
  changes: string;
  limit: string;
  source: HowBuiltSource;
}

function source(label: string, path: string): HowBuiltSource {
  return { label, href: `${GITHUB_MAIN}/${path}` };
}

export const HOW_BUILT = {
  hero: {
    kicker: "How I built Convoful",
    title: "I kept forgetting what I wanted to talk about.",
    lede:
      "The material was already sitting in Instagram saves, X bookmarks, WhatsApp links, screenshots, and the iPhone Share sheet. The problem was getting the right prompt in front of me when Maya and I were together. Convoful is the system I built to do that.",
    shortVersion: [
      "I save one item, choose an audience, then choose Conversation Cards, Library, or both.",
      "Conversation Cards stay face down until that audience is together. Library items are for reading, listening, or watching separately.",
      "The native apps keep offline working copies; a Cloudflare Worker authorizes changes and D1 stores the canonical records.",
      "Account creation and sign-in share one magic-link contract. Live rollout and end-user acceptance belong in the Roadmap and their GitHub issues, not in a frozen claim on this page.",
    ],
  },
  historicalMeasurement: {
    measuredAt: "July 30, 2026",
    canonicalCards: 1027,
    mediaReferences: 213,
    cardsWithMedia: 179,
    instagramCards: 770,
    xCards: 120,
    whatsappCards: 131,
    shareSheetCards: 5,
    unknownCards: 1,
    liveLibraryPublications: 0,
    copy:
      "Read-only production aggregates measured July 30, 2026. They are a dated example of the source mix, not current product or release status.",
  },
  sections: [
    {
      id: "product",
      nav: "Product",
      kicker: "Start with the product",
      title: "One saved item can do two different jobs.",
      summary:
        "For Maya, I can put the same saved item in the face-down deck for our next conversation, publish a recipient-safe Library copy for her to read or watch alone, or do both. Those choices are audience assignments, not privacy labels painted onto one public record.",
      sources: [
        source("Product brief", "docs/GOAL_BRIEF.md"),
        source("Canonical card model", "Agenda/Models/AgendaCard.swift"),
        source("Audience and destination schema", "cloudflare/migrations/0004_audiences_authorization.sql"),
      ],
    },
    {
      id: "stack",
      nav: "Stack",
      kicker: "Actual stack",
      title: "Six runtime pieces, kept in one repository.",
      summary:
        "The shipped system is a SwiftUI iPhone app and Share extension, a Kotlin/Compose Android app, a TypeScript/Vite website, a thin Web gateway, a TypeScript API Worker, and Cloudflare D1/R2 storage. Migrations, importers, release scripts, and tests sit beside them.",
      sources: [
        source("Repository map", "README.md"),
        source("iOS project", "project.yml"),
        source("Android build", "androidApp/build.gradle.kts"),
        source("Web package", "web-client/package.json"),
        source("Web gateway", "convoful-web/wrangler.jsonc"),
        source("Backend production config", "cloudflare/wrangler.production.jsonc"),
      ],
    },
    {
      id: "data-flow",
      nav: "Data flow",
      kicker: "What lives where",
      title: "The phone writes first; the server decides what is canonical.",
      summary:
        "iPhone persists a JSON working copy and Android persists Room rows, so an active conversation can continue offline. Sync sends mutations with a stable retry ID; the API Worker maps the device or browser session to a user, applies each accepted mutation once, appends a sequence number, and returns only authorized records.",
      sources: [
        source("iOS persistence", "Agenda/Store/AgendaStore.swift"),
        source("iOS sync loop", "Agenda/Sync/AgendaSync.swift"),
        source("Canonical schema", "cloudflare/migrations/0001_initial.sql"),
        source("Native sync retry receipts", "cloudflare/migrations/0002_device_sync.sql"),
        source("Owner-workspace response replay", "cloudflare/migrations/0005_owner_workspace_mutations.sql"),
        source("Conversation response replay", "cloudflare/migrations/0009_conversation_sessions.sql"),
        source("Cloudflare architecture", "cloudflare/README.md"),
      ],
    },
    {
      id: "model",
      nav: "Object map",
      kicker: "Tables and payloads in plain English",
      title: "The database nouns behind each screen.",
      summary:
        "The backend calls the thing Mark saved a card; the Web client still calls the owner-side view a bookmark. The map below says what each object means, who can change it, which identity must stay fixed, and the current limit that matters in the product.",
      sources: [
        source("Base data schema", "cloudflare/migrations/0001_initial.sql"),
        source("People, audiences, and sharing", "cloudflare/migrations/0004_audiences_authorization.sql"),
        source("Account sign-in", "cloudflare/migrations/0008_magic_link_accounts.sql"),
        source("Self-service signup", "cloudflare/migrations/0015_self_service_magic_link_signup.sql"),
        source("Conversation history", "cloudflare/migrations/0009_conversation_sessions.sql"),
      ],
    },
    {
      id: "media",
      nav: "Photos & media",
      kicker: "Two different photo stories",
      title: "A screenshot attached to an item is not a card-back photo.",
      summary:
        "Saved-item media belongs to the thing I saved. Card-back photos come from the phone only to make the face-down deck feel like ours. They do not describe the prompt, are stored separately on that device, and are never uploaded by the card-back feature.",
      sources: [
        source("Saved-item media array", "Agenda/Models/AgendaCard.swift"),
        source("Ordered source-media contract", "cloudflare/migrations/0014_owner_source_media.sql"),
        source("iPhone Share input", "project.yml"),
        source("Web New Card boundary", "web-client/src/new-card.ts"),
        source("Rich X import boundary", "docs/X_RICH_IMPORT.md"),
      ],
    },
    {
      id: "search",
      nav: "Search",
      kicker: "The ranking calculation",
      title: "Audience filter first; keyword, Summary, and vector scores second.",
      summary:
        "A card must first belong to the selected audience and destination. The designed online rank is 50% semantic similarity, 25% keyword match, and 25% private Summary match. Notes are excluded. Because production has no semantic route or populated vector index, current clients renormalize keyword and Summary to 50/50.",
      sources: [
        source("Ranking contract", "docs/HYBRID_SEARCH_RANKING.md"),
        source("Quality evaluation", "docs/SEARCH_QUALITY_EVALUATION.md"),
        source("iOS search adapter", "Agenda/Search/AgendaSearchAdapter.swift"),
        source("Enrichment status", "docs/SEARCH_ENRICHMENT.md"),
      ],
    },
    {
      id: "imports-sync",
      nav: "Imports & sync",
      kicker: "How the collection got here",
      title: "Imports made the first 1,021 measured records; Share and New Card added the rest.",
      summary:
        "The July 30, 2026 measurement combines 770 Instagram saves, 131 WhatsApp items, 120 X items, five Share-extension saves, and one unclassified legacy record. Import ledgers preserve where a historical item came from; ordinary saves use owner creation and sync mutations.",
      sources: [
        source("Original source ingestion", "docs/SOURCE_INGESTION.md"),
        source("Instagram import ledger", "docs/INSTAGRAM_SAVED_IMPORT.md"),
        source("X rich importer", "scripts/x_rich_import.py"),
        source("Sync change schema", "cloudflare/migrations/0001_initial.sql"),
      ],
    },
    {
      id: "workflow",
      nav: "How we work",
      kicker: "Mark + Codex",
      title: "An issue owns the work; a pull request owns the proposed change.",
      summary:
        "AGENTS.md defines the shared rules. GitHub carries scope and status. One agent claims one issue and isolated worktree, commits recoverable checkpoints, and asks a different agent to review the exact head. A passing test, simulator run, Apple-valid upload, tester assignment, phone install, and physical acceptance are recorded as different facts.",
      sources: [
        source("Agent operating agreement", "AGENTS.md"),
        source("TestFlight upload script", "scripts/upload_testflight.sh"),
        source("Device evidence", "docs/DEVICE_VERIFICATION.md"),
      ],
    },
    {
      id: "lessons",
      nav: "Lessons",
      kicker: "What worked and what did not",
      title: "The failures were concrete: stale requests, Apple gates, and unproven handoffs.",
      summary:
        "Small owned changes moved quickly. Work stalled when multiple agents repeated obsolete requests, when a long status update hid the one thing Mark needed to do, or when an Apple-valid upload was mistaken for a build a tester could actually install.",
      sources: [
        source("Operating agreement", "AGENTS.md"),
        source("Apple release evidence", "docs/APPLE_RELEASE_ACCESS.md"),
        source("Current roadmap policy", "AGENTS.md"),
      ],
    },
    {
      id: "now",
      nav: "Now & next",
      kicker: "Where current truth lives",
      title: "The Roadmap carries product and release status.",
      summary:
        "Repository source, signed archive, Apple processing, tester assignment, installation, and physical acceptance are separate evidence. Build, device, account, and rollout facts change too quickly to freeze into this technical guide. The Roadmap records user-visible shipped, active, and blocked work; the linked issues carry exact evidence; For Mark carries only the current action or decision that cannot proceed independently.",
      sources: [
        { label: "Public roadmap", href: "https://convoful.com/roadmap" },
        source("Current source build identity", "project.yml"),
        { label: "Build train and device evidence", href: "https://github.com/markitics/agenda/issues/315" },
        { label: "Signed-out Start acceptance", href: "https://github.com/markitics/agenda/issues/327" },
        { label: "Self-service signup evidence", href: "https://github.com/markitics/agenda/issues/323" },
        source("Release goal", "docs/ACTIVE_GOAL.md"),
        source("Operating agreement", "AGENTS.md"),
      ],
    },
  ] satisfies HowBuiltSectionMeta[],
  stack: [
    {
      name: "iPhone",
      detail:
        "Swift 6 and SwiftUI, plus an iOS Share extension. AgendaStore reads and atomically replaces a JSON working copy; local card-back photos and cached private media live in app-private files.",
      source: source("iOS project", "project.yml"),
    },
    {
      name: "Android",
      detail:
        "Kotlin, Jetpack Compose, and Room. The Android app keeps a native offline working copy and follows the same server-owned identities.",
      source: source("Android entities", "androidApp/src/main/java/com/markmoriarty/agenda/data/AgendaEntities.kt"),
    },
    {
      name: "Web",
      detail:
        "TypeScript, Vite, and plain DOM/CSS. The public site and signed-in app ship as static assets behind a small Cloudflare Worker gateway.",
      source: source("Web package", "web-client/package.json"),
    },
    {
      name: "Backend",
      detail:
        "A TypeScript Cloudflare Worker maps credentials to principals, rejects unauthorized card/audience access, applies idempotent sync mutations, serves the owner workspace, creates recipient-safe Library views, and gates private media. A Queue and Workers AI exist for explicitly authorized enrichment jobs; the production automatic-enqueue flag is false.",
      source: source("Worker entry point", "cloudflare/src/index.ts"),
    },
    {
      name: "Storage",
      detail:
        "Cloudflare D1 holds the canonical rows and ordered sync log. A private R2 bucket holds protected source-media objects. The public-site Worker has only a service binding to the API Worker; its committed configuration has no D1 or R2 binding.",
      source: source("Web gateway config", "convoful-web/wrangler.jsonc"),
    },
    {
      name: "Delivery & verification",
      detail:
        "GitHub issues and pull requests, Xcode/TestFlight, Gradle, Vitest, Playwright, platform UI tests, and focused production read-backs.",
      source: source("Repository scripts", "package.json"),
    },
  ] satisfies HowBuiltStackItem[],
  platforms: [
    {
      name: "iPhone",
      state:
        "The SwiftUI app and Share extension keep an offline working copy and use the same server-owned principal, audience, assignment, publication, and session identities as the other clients. Release evidence records source, archive, Apple processing, tester visibility, installation, and direct acceptance separately.",
      limit:
        "A directly accepted slice never proves every iPhone feature. The current Share extension accepts at most one image per save.",
      source: { label: "Build train evidence", href: "https://github.com/markitics/agenda/issues/315" },
    },
    {
      name: "Android",
      state:
        "The native app has an offline Room copy, Conversation Cards, owner capture/recovery, source and order controls, and bounded local card-back photos.",
      limit:
        "It is not a Play Store release or current physical-device acceptance. Production semantic scores are unavailable, so Android Search remains lexical/offline.",
      source: source("Android application", "androidApp/src/main/java/com/markmoriarty/agenda/AgendaApplication.kt"),
    },
    {
      name: "Web",
      state:
        "The Cloudflare-hosted app has authenticated owner editing, a face-down Conversation Card deck, Library surfaces, New Card, Roadmap, and For Mark.",
      limit:
        "Ordinary-user onboarding is not complete. A Web photo draft stays browser-local because authenticated photo saving and sync are not yet available.",
      source: source("Web application", "web-client/src/app.ts"),
    },
  ],
  product: {
    terms: [
      {
        name: "Saved item",
        detail:
          "The human umbrella term for something Mark saved. The backend and native apps call the canonical record a card; the Web client historically calls the same owner record a bookmark.",
      },
      {
        name: "Conversation Card",
        detail:
          "A saved item assigned to an audience for together-time. It stays face down until that audience is together.",
      },
      {
        name: "Library publication",
        detail:
          "A separate, revocable recipient-facing copy created from a saved item. It can carry Shared Notes, but never exposes the owner's private Notes, Summary, keywords, or Conversation Cards.",
      },
    ],
    origin:
      "I was not trying to build another bookmark manager. I wanted a deck that could answer, “What did I want to talk about with Maya?” when we were actually in the same room. Library came later for material that should not wait for the next conversation.",
  },
  dataFlow: {
    steps: [
      {
        name: "1. Capture",
        detail:
          "Mark saves a link, thought, image, or imported post. Native apps write to their local working copy first; the Web New Card flow can keep an unfinished photo draft in browser storage.",
      },
      {
        name: "2. Authorize and sync",
        detail:
          "The client sends its device or browser-session credential plus a stable mutation ID. The API maps that credential to a principal, rejects revoked or out-of-scope access, stores the request hash and applied status against that mutation ID, and appends an accepted change to sync_changes. An exact native-sync retry returns duplicate and does not append the change again; it does not replay the original result body or sequence. Owner-workspace saves and conversation-session commands use separate idempotency records that do store response JSON for exact replay.",
      },
      {
        name: "3. Store the canonical copy",
        detail:
          "D1 stores cards, scopes, audiences, assignments, publications, receipts, sessions, and the ordered change log. Source-media bytes can live in private R2 while D1 keeps ownership, position, lifecycle, and integrity metadata. The public Web Worker forwards API requests through a service binding and has no direct D1 or R2 binding.",
      },
      {
        name: "4. Project only what was intended",
        detail:
          "Owner devices can recover the private saved item. An audience assignment makes it a Conversation Card, a separate Library publication, or both. Recipient responses never contain owner-private fields.",
      },
    ],
    local: [
      "iPhone: a JSON working copy, queued changes, app-private media, and decorative card-back photos.",
      "Android: a Room working copy and local sync state.",
      "Browser: static app code plus temporary IndexedDB drafts for an unfinished New Card photo.",
    ],
    cloud: [
      "D1: people, devices, saved items, audiences, assignments, publications, progress, import provenance, conversation history, and ordered sync records.",
      "Private R2: protected media objects and source attachments when a reviewed workflow uploads them.",
      "Cloudflare processes and hosts this data. An authorized operator can read it when operating, debugging, recovering, or deleting the service.",
    ],
    encryption:
      "App and browser traffic uses HTTPS to Cloudflare. The repository does not implement client-held content encryption; the API Worker and an authorized operator can read the server-side records needed to run or recover the service. Convoful is not end-to-end encrypted or zero knowledge.",
    retention: [
      "The schema records account-deletion requests, but there is no complete self-service export or verified delete-everything product flow yet.",
      "Canonical D1 rows and import ledgers have no product expiry date; an authorized workflow must deliberately remove them.",
      "Deleting a live D1 row or R2 object is not a promise that every Cloudflare recovery copy disappears immediately.",
      "The committed production R2 configuration declares no custom lifecycle or retention lock, so ordinary objects remain until explicitly deleted.",
    ],
  },
  media: {
    savedItem:
      "AgendaCard carries a media array, and the source-media schema allows positions 0 through 3: up to four ordered source assets for one saved item. Current creation and viewing surfaces do not expose that full contract. The iPhone Share extension accepts one image per share, signed-in Web New Card offers Link and Text only, a recovered Web photo draft cannot save or sync, and current card screens normally render the first usable image.",
    screenshots:
      "So Mark's three screenshots can fit the underlying model, but ordinary product UI cannot yet make them one dependable three-image saved item. Sharing them from iPhone today creates three separate saved items unless a reviewed richer importer groups them.",
    cardBack:
      "Card-back photos make the face-down deck personal by shuffling photos from the phone behind different cards. They are decorative and have no relationship to the card's title, Notes, Summary, URL, or attached media. The card-back feature resizes them and keeps them in app-private local storage; they are never uploaded.",
  },
  search: {
    designed:
      "After audience and destination filters, the designed online score combines semantic similarity, explicit keywords, and the owner's private Summary.",
    formula: [
      "designed = 0.50 × semantic + 0.25 × keyword + 0.25 × Summary",
      "current fallback = 0.50 × keyword + 0.50 × Summary",
    ],
    current:
      "Automatic enrichment is off, and production has no authenticated semantic-search route or populated vector index. The live clients therefore use the lexical fallback. Private Notes are excluded from every search path.",
    evaluation: {
      benchmark:
        "The private-safe starter benchmark contains 239 documents, 26 judged queries, and 30 strong pairwise preferences. The fixed product profile scores 0.927913.",
      nearbyProfiles:
        "A keyword-forward 50/30/20 experiment scores 0.928295: +0.000382 versus product, with paired 95% interval [0.000000, 0.001060]. A Summary-forward 30/30/40 experiment scores 0.923257 with interval [-0.012938, -0.000265]. A diagnostic profile that improperly adds private Notes scores 0.922110; Notes remain excluded.",
      sensitivity:
        "An exhaustive 231-profile post-hoc grid finds 60/35/5 at 0.933252, or +0.005339. That challenger was selected and measured on the same labeled set. Under the fixed Summary-pending stress, product retains 0.500000 of preferences while the challenger retains 0.233333, a -0.266667 difference.",
      holdout:
        "The deciding blinded holdout has not run. A future 15–20-query pilot is frozen to compare only 50/25/25 and 60/35/5 without retuning. Until independent labels and the pending-Summary guardrail agree, runtime stays 50/25/25.",
      relevancePilot:
        "A separate 77-item top/middle/tail pilot found 3 relevant, 61 irrelevant, and 13 uncertain rows. SearchAgent supplied those labels, so they are directional evidence—not independent ground truth or a whole-result precision claim.",
      enrichment:
        "A historical zero-production-write enrichment rehearsal produced valid Summary, keywords, and a 768-dimensional embedding for 239 of 246 selected items. Five lacked usable evidence, two repeatedly failed structured output, and seven successes were flagged for review. No missing metadata was invented.",
    },
  },
  imports: {
    sources: [
      { name: "Instagram", count: 770, detail: "one-time historical import" },
      { name: "WhatsApp", count: 131, detail: "reviewed historical import" },
      { name: "X", count: 120, detail: "bookmarks and focused imports" },
      { name: "Share extension", count: 5, detail: "ordinary new saves" },
      { name: "Unknown", count: 1, detail: "legacy source not confidently classified" },
    ],
    sync:
      "Each accepted server change gets an increasing sequence number. After applying change 1,090, for example, a device sends cursor=1090 and the Worker selects only rows whose sequence is greater than 1,090. The response's next_cursor becomes the next request's cursor. It is a bookmark in the change log—not a card count, date, upload status, or proof that device-only photos were backed up.",
  },
  workflow: {
    stages: [
      {
        name: "Plan",
        detail:
          "Mark's request becomes or amends a GitHub issue. Milestones group release goals. The public Roadmap deliberately selects only user-safe outcomes; it does not mirror engineering labels or agent logs.",
      },
      {
        name: "Build",
        detail:
          "One agent records its task ID, issue, branch, worktree, file seam, and exclusions in GitHub and the local collision ledger. Small commits keep partial progress recoverable.",
      },
      {
        name: "Review",
        detail:
          "A different Codex agent reviews an exact commit and tree. If the author pushes a correction, the prior verdict no longer covers the head and review starts again.",
      },
      {
        name: "Prove",
        detail:
          "Tests and a simulator or stable dedicated device make expected behavior repeatable. An affected iPhone stays in its reported state for diagnosis and later in-place acceptance; resetting it for a clean run would destroy different evidence. Neither lane substitutes for the other. An Apple-valid upload proves processing, Mark Internal assignment proves tester visibility, a device read-back proves installation, and a direct tap-through proves the feature on that build.",
      },
      {
        name: "Communicate",
        detail:
          "If a real decision or account action needs Mark, the agent writes one active For Mark item and sends the same request by the responsible role's email lane. Routine PR updates stay in GitHub; resolved requests are cleared instead of repeated.",
      },
    ],
    roles: [
      {
        name: "MasterAgent",
        detail:
          "Coordinates shared contracts and release order, owns iPhone work, keeps the Cloudflare API compatible, and reconciles Roadmap and For Mark truth. It does not absorb every platform change.",
        source: source("MasterAgent boundary", "AGENTS.md"),
      },
      {
        name: "WebAgent",
        detail:
          "Owns implementation in the public site and signed-in Web client. It coordinates shared routes, account behavior, and API contracts with MasterAgent before changing them.",
        source: source("WebAgent boundary", "AGENTS.md"),
      },
      {
        name: "SearchAgent",
        detail:
          "Owns ranking, evaluation, Summary and keyword editing, and the semantic-enrichment rollout until GitHub records a handoff. A Search experiment is not a platform release by itself.",
        source: source("SearchAgent boundary", "AGENTS.md"),
      },
      {
        name: "AndroidAgent",
        detail:
          "Owns Android-native implementation and Android-specific emulator, device, signing, and Play evidence. Shared server contracts still require coordination with MasterAgent.",
        source: source("AndroidAgent boundary", "AGENTS.md"),
      },
      {
        name: "MarketingAgent",
        detail:
          "Owns positioning, public copy briefs, audience research, and launch ideas. It can recommend a change, but the relevant platform owner implements and verifies product code.",
        source: source("MarketingAgent boundary", "AGENTS.md"),
      },
    ],
    releaseCheckpoints:
      "The six-hour wake-up is a readiness and review checkpoint, not a release schedule. A build is eligible only when it carries a user-visible feature or meaningful bug fix that is complete, recoverable, and independently reviewed. If nothing substantive is ready, no build ships; agents continue backlog work. One focused issue stays with one owner in an isolated branch and worktree with an explicit file seam, a different agent reviews the exact head, and MasterAgent decides integration order. Source merge, signed archive, Apple processing, Mark Internal assignment, installation, and physical acceptance remain separate gates.",
  },
  lessons: {
    worked: [
      "One issue, one owner, one isolated worktree, and an exact-head review before merge.",
      "A local iPhone JSON file and Android Room database for offline use, backed by one server-owned identity and assignment model.",
      "Synthetic fixtures and aggregate read-backs that can prove behavior without putting a real card, Note, prompt, token, or email in a screenshot.",
      "Separate Conversation assignments and Library publications, so recipient APIs never need to return the owner's Notes or Summary.",
    ],
    failed: [
      "Long task updates buried the requested action, so important gates moved to a single For Mark queue plus matching email.",
      "Repeated DONT_NOTIFY messages and already-resolved action requests became noise; the fix was to keep empty monitoring silent and clear obsolete requests.",
      "Apple-valid did not mean tester-visible, and tester-visible did not mean installed. Recording each state separately stopped one green surface from standing in for the next.",
      "Earlier versions of this page used slogans about local storage instead of naming the JSON file, Room database, D1 tables, R2 objects, and browser-only draft that actually exist.",
    ],
    withoutMark:
      "If Mark is unavailable, agents continue safe, reversible work: code, tests, documentation, review, simulator checks, and read-only inspection. They park only the exact choice, signed-in account action, physical-device step, spending decision, or destructive action that genuinely requires him; unrelated work continues.",
  },
  current: {
    copy:
      "This page explains the architecture and the release rules; it is deliberately not a second status dashboard. Use the Roadmap for current product state and the linked GitHub issues for exact source, deployment, account, device, and acceptance evidence. For Mark names only the action or decision that currently needs Mark; independent backlog work continues around it.",
    links: [
      { label: "See the curated Roadmap", href: "https://convoful.com/roadmap" },
      { label: "Open For Mark", href: "https://convoful.com/for-mark" },
    ],
  },
  model: [
    {
      name: "Person and account",
      codeName: "users + user_email_identities",
      meaning: "The human principal whose private workspace and memberships are being authorized.",
      importantFields: "Stable ID, display name, verified email identity, disabled state.",
      ownedBy: "The service owns identity; the person signs in and manages sessions.",
      changes: "Display/account state can change; the account-to-email identity mapping is intentionally stable.",
      limit: "The schema supports verified email identities and controlled account creation. Deployment, fixture provisioning, email delivery, session creation, public rollout, and end-user acceptance are separate gates tracked in issue #323; source alone does not prove any of them live.",
      source: source("Self-service signup schema", "cloudflare/migrations/0015_self_service_magic_link_signup.sql"),
    },
    {
      name: "Device session",
      codeName: "app_devices",
      meaning: "One revocable iPhone, Android device, or browser session acting for a person.",
      importantFields: "Device ID, label, principal, enrollment/last-seen time, revocation.",
      ownedBy: "Created by the service after an approved enrollment or sign-in.",
      changes: "Last-seen and revocation change; credentials never appear in product responses.",
      limit: "A revoked device stops authorizing requests.",
      source: source("Device schema", "cloudflare/migrations/0001_initial.sql"),
    },
    {
      name: "Saved item",
      codeName: "cards / bookmark",
      meaning: "The canonical thing Mark saved: a link, text thought, image, or imported post.",
      importantFields: "Source, timestamps, URLs, media, private Notes, source text, Summary, keywords, provenance.",
      ownedBy: "Exactly one owner principal through an immutable card scope.",
      changes: "Owner fields and destinations can change; identity and owner scope do not silently move.",
      limit: "Historical compatibility still uses card/bookmark names in code.",
      source: source("Card model", "Agenda/Models/AgendaCard.swift"),
    },
    {
      name: "Media asset",
      codeName: "media_assets + AgendaCard.media",
      meaning: "An image or video reference belonging to a saved item.",
      importantFields: "Card, kind, private object key or remote source, type, size, ordered payload position.",
      ownedBy: "Follows the saved item's owner and authorization.",
      changes: "Several assets may belong to one item; bytes and references are handled separately from card text.",
      limit: "Not every input surface can create a multi-image item yet.",
      source: source("Media schema", "cloudflare/migrations/0001_initial.sql"),
    },
    {
      name: "Audience",
      codeName: "audiences + audience_memberships",
      meaning: "A person, group, or context such as Maya that can receive assignments.",
      importantFields: "Owner, display name, position, active members, archived state.",
      ownedBy: "One owner creates it; accepted members determine who may receive shared material.",
      changes: "Names/order/membership can change; audience ownership is stable.",
      limit: "An archived or lost membership disappears from authorized views.",
      source: source("Audience schema", "cloudflare/migrations/0004_audiences_authorization.sql"),
    },
    {
      name: "Assignment",
      codeName: "audience_assignments",
      meaning: "The link between one saved item and one audience, including its destination.",
      importantFields: "Saved item, audience, primary flag, Conversation Cards on/off, Library on/off.",
      ownedBy: "The saved item's owner edits the complete desired set.",
      changes: "Destinations can change; the server owns stable assignment IDs.",
      limit: "At least one destination must remain enabled for an active assignment.",
      source: source("Assignment schema", "cloudflare/migrations/0004_audiences_authorization.sql"),
    },
    {
      name: "Library publication",
      codeName: "library_publications",
      meaning: "A recipient-safe copy deliberately shared through one Library-enabled assignment.",
      importantFields: "Shared title, URL, source label, Shared Notes, format, Read/Listen/Watch lane.",
      ownedBy: "The saved-item owner publishes; recipients cannot see owner-private Notes or Summary.",
      changes: "The shared copy can update or be revoked without changing the private source item.",
      limit: "Production currently has no live recipient publications.",
      source: source("Publication schema", "cloudflare/migrations/0004_audiences_authorization.sql"),
    },
    {
      name: "Recipient progress",
      codeName: "library_receipts",
      meaning: "One person's New/Open/In progress/Finished/Dismissed state for one Library publication.",
      importantFields: "Publication, person, state, progress timestamps, revision.",
      ownedBy: "The authenticated recipient owns only their own receipt.",
      changes: "Progress changes independently of the owner's private workspace.",
      limit: "A receipt is visible only while the audience/publication relationship remains eligible.",
      source: source("Receipt schema", "cloudflare/migrations/0006_recipient_library_receipts.sql"),
    },
    {
      name: "Search metadata",
      codeName: "human_summary + keywords + enrichment jobs",
      meaning: "Owner-private material used to make saved items findable.",
      importantFields: "Summary, keywords, status, fingerprints, optional embedding metadata.",
      ownedBy: "The owner edits Summary/keywords; the service may process an explicitly authorized job.",
      changes: "A Summary edit immediately makes the old semantic vector stale.",
      limit: "Notes are excluded; automatic enrichment is off and no production semantic index is live.",
      source: source("Enrichment schema", "cloudflare/migrations/0003_search_enrichment.sql"),
    },
    {
      name: "Conversation and outcome history",
      codeName: "conversation_sessions + events + session cards",
      meaning: "A dated conversation for one audience and the reveal/discuss/postpone history inside it.",
      importantFields: "Audience, ordered assignments, revision, events, outcome, end reason.",
      ownedBy: "The owner starts and acts on a session from an authorized device.",
      changes: "Events append; corrections point at prior events instead of rewriting history invisibly.",
      limit: "Ending a canonical session is final.",
      source: source("Conversation schema", "cloudflare/migrations/0009_conversation_sessions.sql"),
    },
    {
      name: "Sync and retry record",
      codeName: "sync_changes + mutation receipts",
      meaning: "The ordered change log and replay protection that keep retries from duplicating a save.",
      importantFields: "Change sequence/entity/operation; mutation ID, device, payload hash, pending/applied status, timestamps.",
      ownedBy: "The service assigns change sequences and records each native mutation's payload hash and applied state.",
      changes: "New changes append; a repeated exact native-sync request returns duplicate without appending another change.",
      limit: "Native sync receipts do not store response JSON. Owner-workspace and conversation-session mutations use separate response-replay tables. A cursor still says nothing about device-only photos.",
      source: source("Sync schema", "cloudflare/migrations/0002_device_sync.sql"),
    },
    {
      name: "Import provenance",
      codeName: "source collections, occurrences, runs, aliases",
      meaning: "The private record of where historical saves came from and how duplicates were reconciled.",
      importantFields: "Source identity, capture fingerprints, positions, run state, canonical card mapping.",
      ownedBy: "Operator tooling stages and reviews it for the owner's collection.",
      changes: "Runs advance through monotonic states; sealed reviewed material becomes immutable.",
      limit: "Private source text and capture details never become public evidence.",
      source: source("Import schema", "cloudflare/migrations/0007_instagram_source_imports.sql"),
    },
  ] satisfies HowBuiltModelItem[],
} as const;

export function howBuiltSection(id: string): HowBuiltSectionMeta {
  const section = HOW_BUILT.sections.find((item) => item.id === id);
  if (!section) throw new Error(`Unknown How I Built This section: ${id}`);
  return section;
}
